"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const WidgetError_1 = require("../Error/WidgetError");
/**
 * Restricted service ids.
 *
 * @type {string[]}
 */
const RESTRICTED_IDS = ["container"];
/**
 * Service definition.
 */
class ServiceDefinition {
    /**
     * Constructs service definition.
     *
     * @param {string} id
     * @param classReference
     */
    constructor(id, classReference) {
        /**
         * Service tags.
         */
        this.tags = {};
        /**
         * Class constructor arguments.
         */
        this.argumentList = [];
        /**
         * Whether it's shared between other services.
         */
        this.shared = true;
        if (RESTRICTED_IDS.includes(id)) {
            throw new WidgetError_1.default(`Services cannot have '${id}' as ID.`);
        }
        this.id = id;
        this.classReference = classReference;
    }
    /**
     * Clones the service definition.
     */
    clone() {
        const clone = new ServiceDefinition(this.getId(), this.getClass());
        clone.argumentList = Array.from(this.argumentList);
        clone.tags = Object.assign({}, this.tags);
        clone.shared = this.shared;
        clone.parent = this.parent;
        return clone;
    }
    /**
     * Creates definition from object.
     *
     * @param {string} id
     * @param {*} definition
     *
     * @return {ServiceDefinition}
     */
    static fromObject(id, definition) {
        const def = new ServiceDefinition(id, definition.class);
        // Clone the definition since we are going to delete keys.
        definition = Object.assign({}, definition);
        delete definition.class;
        // @TODO: transformer.
        const mapper = {
            argumentList: "setArguments",
            tags: "setTags",
            shared: "setShared",
            parent: "setParent"
        };
        Object.entries(mapper).forEach(([key, setter]) => {
            if (definition[key] === undefined) {
                return;
            }
            def[setter](definition[key]);
            delete definition[key];
        });
        Object.keys(definition).forEach((key) => {
            throw new Error(`Unknown service definition key '${key}'.`);
        });
        return def;
    }
    /**
     * Gets the ID of the service.
     *
     * @return {string}
     */
    getId() {
        return this.id;
    }
    /**
     * Gets the class.
     *
     * @return {*}
     */
    getClass() {
        return this.classReference;
    }
    /**
     * Sets the class of the service.
     *
     * @param {*} classReference
     * @return {ServiceDefinition}
     */
    setClass(classReference) {
        this.classReference = classReference;
        return this;
    }
    /**
     * Sets as shared.
     *
     * @param {boolean} shared
     * @return {ServiceDefinition}
     */
    setShared(shared) {
        this.shared = shared;
        return this;
    }
    /**
     * Determines if it's shared between other services.
     *
     * @return {boolean}
     */
    isShared() {
        return this.shared;
    }
    /**
     * Gets parent.
     *
     * @return {string}
     */
    getParent() {
        return this.parent;
    }
    /**
     * Sets parent.
     *
     * @param {string} parent
     * @return {ServiceDefinition}
     */
    setParent(parent) {
        this.parent = parent;
        return this;
    }
    /**
     * Sets arguments.
     *
     * @param {*[]} args
     * @return {this}
     */
    setArguments(args) {
        this.argumentList = args;
        return this;
    }
    /**
     * Sets argument.
     *
     * @param {number} id
     * @param arg
     * @return {this}
     */
    setArgument(id, arg) {
        if (id < 0 && id >= this.argumentList.length) {
            throw new WidgetError_1.default(`Argument index out of bound.`);
        }
        this.argumentList[id] = arg;
        return this;
    }
    /**
     * Gets all arguments.
     *
     * @return {*[]}
     */
    getArguments() {
        return this.argumentList;
    }
    /**
     * Pushes argument at the end.
     *
     * @param {*} arg
     * @return {ServiceDefinition}
     */
    pushArgument(arg) {
        this.argumentList.push(arg);
        return this;
    }
    /**
     * Pushes argument at the beginning.
     *
     * @param {*} arg
     * @return {ServiceDefinition}
     */
    unshiftArgument(arg) {
        this.argumentList.unshift(arg);
        return this;
    }
    /**
     * Sets tag.
     *
     * @param {string} name
     * @param {*} tagData
     * @return {ServiceDefinition}
     */
    setTag(name, tagData = {}) {
        this.tags[name] = tagData;
        return this;
    }
    /**
     * Sets tags.
     *
     * @param {*} tags
     * @return {ServiceDefinition}
     */
    setTags(tags) {
        this.tags = tags;
        return this;
    }
    /**
     * Gets tags.
     *
     * @return {*}
     */
    getTags() {
        return this.tags;
    }
    /**
     * Get tag.
     *
     * @param {string} name
     * @return {*}
     */
    getTag(name) {
        return this.tags[name];
    }
    /**
     * Determines if tag exists.
     *
     * @param {string} name
     * @return {ServiceDefinition}
     */
    hasTag(name) {
        return !!this.tags[name];
    }
}
exports.default = ServiceDefinition;
